import axios from 'axios';
import config from '../config';
import reportsService from '../api/reportsService';
import type { Reports } from '../types/types';
import { t } from '../i18n/t';
import {
    buildReportXlsxFilename,
    parseFilenameFromContentDisposition,
    type ReportFilenameUser,
} from './reportFilename';
const url = config.api.baseUrl;

export const getErrorMessage = (err: unknown, fallback: string): string => {
    if (axios.isAxiosError(err)) {
        const data = err.response?.data;
        if (data && typeof data === 'object' && !(data instanceof Blob)) {
            const msg =
                (data as { message?: string; error?: string }).message ??
                (data as { message?: string; error?: string }).error;
            if (typeof msg === 'string') return msg;
        }
        const code = err.response?.status;
        return code ? `${fallback} (${code})` : (err.message || fallback);
    }
    return err instanceof Error ? err.message : fallback;
};

export const downloadReportXls = async (
    report: Reports,
    userForFilename: ReportFilenameUser | null | undefined,
    withFilter = false,
): Promise<void> => {
    const params = new URLSearchParams({
        value: report.value,
        withFilter: String(withFilter),
    });
    if (report.pin) params.set('pin', report.pin);
    const downloadUrl = `${url}/reports/excel?${params.toString()}`;
    const response = await fetch(downloadUrl);
    if (!response.ok) {
        const text = await response.text();
        throw new Error(text || t('reportDetail.errorStatus', { status: response.status }));
    }
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download =
        buildReportXlsxFilename(userForFilename, report.created_at, report.id) ||
        parseFilenameFromContentDisposition(response.headers.get('Content-Disposition')) ||
        'report.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
};

export const downloadReportPdf = async (
    report: Reports,
    userId: number,
    short: boolean,
): Promise<void> => {
    const payload: Record<string, unknown> = {
        value: report.value,
        pin: report.pin,
        sampling_rate: '250',
        userId,
    };
    if (report.pin) payload.pin = report.pin;
    else delete payload.pin;
    if (short) payload.short = true;
    // getFileByUserId: admin + иерархия (BE scope); для своих тоже ок
    let response;
    try {
        response = await reportsService.getFileByUserId(
            payload as Parameters<typeof reportsService.getFileByUserId>[0],
            { responseType: 'blob' },
        );
    } catch {
        response = await reportsService.getFile(
            payload as Parameters<typeof reportsService.getFileByUserId>[0],
            { responseType: 'blob' },
        );
    }
    const blob = new Blob([response.data], { type: response.headers['content-type'] });
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    const now = new Date();
    const timestamp = [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, '0'),
        String(now.getDate()).padStart(2, '0'),
        '_',
        String(now.getHours()).padStart(2, '0'),
        String(now.getMinutes()).padStart(2, '0'),
        String(now.getSeconds()).padStart(2, '0'),
    ].join('');
    const contentDisposition = response.headers['content-disposition'];
    const fileNameMatch =
        typeof contentDisposition === 'string' && contentDisposition.match(/filename="?(.+?)"?$/);
    link.download = fileNameMatch ? fileNameMatch[1] : `ecg_report_${report.id}_${timestamp}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
};
